import React from 'react';
import { CheckCircle, AlertCircle, Clock } from 'lucide-react';

interface WebhookHealthProps {
  status: {
    total: number;
    success: number;
    failed: number;
    pending: number;
  };
}

export function WebhookHealth({ status }: WebhookHealthProps) {
  const successRate = status.total > 0 ? (status.success / status.total) * 100 : 0;

  const getStatusColor = () => {
    if (successRate >= 99.5) return '#08d660';
    if (successRate >= 95) return '#ffa500';
    return '#e84c3d';
  };

  const getStatusBgColor = () => {
    if (successRate >= 99.5) return 'rgba(8, 214, 96, 0.1)';
    if (successRate >= 95) return 'rgba(255, 165, 0, 0.1)';
    return 'rgba(232, 76, 61, 0.1)';
  };

  const getStatusBorderColor = () => {
    if (successRate >= 99.5) return '#08d660';
    if (successRate >= 95) return '#ffa500';
    return '#e84c3d';
  };

  return (
    <div
      className="rounded-2xl p-6 backdrop-blur-sm border"
      style={{
        backgroundColor: '#111116',
        borderColor: '#1e1e25',
      }}
    >
      <div className="mb-6">
        <h3 style={{ color: '#f5f5f7' }} className="text-lg font-semibold">
          Webhook Health Status
        </h3>
        <p style={{ color: '#8a8a95' }} className="text-sm">
          Status do sistema de pagamentos
        </p>
      </div>
      <div className="space-y-4">
        <div
          className="p-4 rounded-lg border"
          style={{
            backgroundColor: getStatusBgColor(),
            borderColor: getStatusBorderColor(),
          }}
        >
          <div className="flex items-center justify-between">
            <div>
              <p style={{ color: '#8a8a95' }} className="text-sm mb-1">
                Taxa de Sucesso
              </p>
              <p style={{ color: getStatusColor() }} className="text-3xl font-bold">
                {successRate.toFixed(2)}%
              </p>
            </div>
            {successRate >= 99.5 ? (
              <CheckCircle className="w-12 h-12" style={{ color: '#08d660' }} />
            ) : (
              <AlertCircle className="w-12 h-12" style={{ color: '#e84c3d' }} />
            )}
          </div>
        </div>

        <div className="grid grid-cols-3 gap-3">
          <div
            className="p-3 rounded-lg border"
            style={{
              backgroundColor: 'rgba(8, 214, 96, 0.05)',
              borderColor: '#1e1e25',
            }}
          >
            <div className="flex items-center gap-2 mb-1">
              <CheckCircle className="w-4 h-4" style={{ color: '#08d660' }} />
              <span style={{ color: '#8a8a95' }} className="text-xs">
                Sucesso
              </span>
            </div>
            <p style={{ color: '#08d660' }} className="text-xl font-bold">
              {status.success}
            </p>
          </div>

          <div
            className="p-3 rounded-lg border"
            style={{
              backgroundColor: 'rgba(232, 76, 61, 0.05)',
              borderColor: '#1e1e25',
            }}
          >
            <div className="flex items-center gap-2 mb-1">
              <AlertCircle className="w-4 h-4" style={{ color: '#e84c3d' }} />
              <span style={{ color: '#8a8a95' }} className="text-xs">
                Falhas
              </span>
            </div>
            <p style={{ color: '#e84c3d' }} className="text-xl font-bold">
              {status.failed}
            </p>
          </div>

          <div
            className="p-3 rounded-lg border"
            style={{
              backgroundColor: 'rgba(255, 165, 0, 0.05)',
              borderColor: '#1e1e25',
            }}
          >
            <div className="flex items-center gap-2 mb-1">
              <Clock className="w-4 h-4" style={{ color: '#ffa500' }} />
              <span style={{ color: '#8a8a95' }} className="text-xs">
                Pendentes
              </span>
            </div>
            <p style={{ color: '#ffa500' }} className="text-xl font-bold">
              {status.pending}
            </p>
          </div>
        </div>

        <div
          className="pt-3 border-t"
          style={{ borderColor: '#1e1e25' }}
        >
          <div className="flex justify-between text-sm">
            <span style={{ color: '#8a8a95' }}>Total de Eventos</span>
            <span style={{ color: '#f5f5f7' }} className="font-bold">
              {status.total}
            </span>
          </div>
        </div>

        <div className="pt-2">
          {successRate >= 99.5 ? (
            <p style={{ color: '#08d660' }} className="text-sm">
              ✅ Sistema funcionando normalmente
            </p>
          ) : successRate >= 95 ? (
            <p style={{ color: '#ffa500' }} className="text-sm">
              ⚠️ Alguns webhooks falharam
            </p>
          ) : (
            <p style={{ color: '#e84c3d' }} className="text-sm">
              ❌ Taxa de falha crítica
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
